import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface Template {
  id: string;
  name: string;
  category: string;
  description: string;
  preview: string;
  features: string[];
  platforms: string[];
  isPremium: boolean;
}

const MarketingAdsPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [user, setUser] = useState<any>(null);

  const categories = [
    { id: 'all', name: 'All Templates', count: 24 },
    { id: 'social-media', name: 'Social Media', count: 8 },
    { id: 'display-ads', name: 'Display Ads', count: 6 },
    { id: 'video-ads', name: 'Video Ads', count: 5 },
    { id: 'print-ads', name: 'Print Ads', count: 5 }
  ];

  const templates: Template[] = [
    // Social Media Ads
    {
      id: 'sm-001',
      name: 'Facebook Ad Campaign',
      category: 'social-media',
      description: 'Engaging Facebook ad template with multiple format options',
      preview: 'https://public.readdy.ai/ai/img_res/f00366da51ddc116b12a4aa9e3a5c4ed.jpg',
      features: ['Multiple Formats', 'A/B Testing', 'Audience Targeting', 'Performance Tracking'],
      platforms: ['Facebook', 'Instagram'],
      isPremium: false
    },
    {
      id: 'sm-002',
      name: 'Instagram Story Ads',
      category: 'social-media',
      description: 'Vertical story ad templates optimized for Instagram and Facebook',
      preview: 'https://readdy.ai/api/search-image?query=Instagram%20story%20ad%20design%20with%20vertical%20mobile%20format%2C%20colorful%20gradient%20background%2C%20modern%20social%20media%20graphics%2C%20story%20template&width=300&height=500&seq=sm002&orientation=portrait',
      features: ['Story Format', 'Swipe-Up Actions', 'Brand Integration', 'Mobile Optimized'],
      platforms: ['Instagram', 'Facebook Stories'],
      isPremium: true
    },
    {
      id: 'sm-003',
      name: 'LinkedIn Professional Ads',
      category: 'social-media',
      description: 'Professional B2B advertising templates for LinkedIn campaigns',
      preview: 'https://readdy.ai/api/search-image?query=LinkedIn%20professional%20ad%20design%20with%20business%20graphics%2C%20corporate%20blue%20colors%2C%20professional%20networking%20imagery%2C%20B2B%20marketing&width=400&height=300&seq=sm003&orientation=landscape',
      features: ['B2B Targeting', 'Professional Design', 'Lead Generation', 'Industry Focus'],
      platforms: ['LinkedIn'],
      isPremium: false
    },
    {
      id: 'sm-004',
      name: 'Twitter Promoted Tweets',
      category: 'social-media',
      description: 'Twitter ad templates with engaging copy and visual elements',
      preview: 'https://readdy.ai/api/search-image?query=Twitter%20promoted%20tweet%20design%20with%20Twitter%20blue%20branding%2C%20social%20media%20graphics%2C%20tweet%20format%2C%20engaging%20visual%20content&width=400&height=250&seq=sm004&orientation=landscape',
      features: ['Tweet Format', 'Hashtag Integration', 'Retweet Optimization', 'Trending Topics'],
      platforms: ['Twitter'],
      isPremium: false
    },

    // Display Ads
    {
      id: 'da-001',
      name: 'Google Display Network',
      category: 'display-ads',
      description: 'Responsive display ads for Google Ads network',
      preview: 'https://readdy.ai/api/search-image?query=Google%20display%20ad%20design%20with%20clean%20modern%20layout%2C%20Google%20branding%20colors%2C%20web%20banner%20format%2C%20professional%20advertising%20graphics&width=400&height=300&seq=da001&orientation=landscape',
      features: ['Responsive Design', 'Multiple Sizes', 'Auto-Optimization', 'Conversion Tracking'],
      platforms: ['Google Ads', 'YouTube', 'Gmail'],
      isPremium: false
    },
    {
      id: 'da-002',
      name: 'Banner Ad Collection',
      category: 'display-ads',
      description: 'Complete set of web banner ads in standard sizes',
      preview: 'https://readdy.ai/api/search-image?query=Web%20banner%20ad%20collection%20with%20multiple%20sizes%2C%20professional%20web%20design%2C%20banner%20advertising%20graphics%2C%20digital%20marketing%20visuals&width=400&height=200&seq=da002&orientation=landscape',
      features: ['Standard Sizes', 'Web Optimized', 'Click-Through Design', 'Brand Consistency'],
      platforms: ['Websites', 'Ad Networks'],
      isPremium: true
    },
    {
      id: 'da-003',
      name: 'Retargeting Campaigns',
      category: 'display-ads',
      description: 'Specialized retargeting ad templates to re-engage visitors',
      preview: 'https://readdy.ai/api/search-image?query=Retargeting%20ad%20design%20with%20comeback%20messaging%2C%20personalized%20graphics%2C%20customer%20return%20imagery%2C%20remarketing%20visuals&width=400&height=300&seq=da003&orientation=landscape',
      features: ['Personalization', 'Dynamic Content', 'Visitor Tracking', 'Conversion Focus'],
      platforms: ['Google Ads', 'Facebook', 'Display Networks'],
      isPremium: false
    },

    // Video Ads
    {
      id: 'va-001',
      name: 'YouTube Ad Templates',
      category: 'video-ads',
      description: 'Video ad templates for YouTube advertising campaigns',
      preview: 'https://readdy.ai/api/search-image?query=YouTube%20video%20ad%20template%20with%20video%20player%20interface%2C%20YouTube%20branding%2C%20video%20advertising%20graphics%2C%20play%20button%20design&width=400&height=300&seq=va001&orientation=landscape',
      features: ['Video Scripts', 'Thumbnail Design', 'Call-to-Action', 'Skip Ad Optimization'],
      platforms: ['YouTube', 'Google Video Partners'],
      isPremium: true
    },
    {
      id: 'va-002',
      name: 'Social Video Ads',
      category: 'video-ads',
      description: 'Short-form video ads for social media platforms',
      preview: 'https://readdy.ai/api/search-image?query=Social%20media%20video%20ad%20design%20with%20mobile%20video%20format%2C%20social%20platform%20graphics%2C%20short-form%20video%20elements%2C%20engaging%20visuals&width=300&height=400&seq=va002&orientation=portrait',
      features: ['Short Format', 'Mobile First', 'Auto-Play Ready', 'Sound-Off Optimization'],
      platforms: ['Facebook', 'Instagram', 'TikTok', 'Twitter'],
      isPremium: false
    },
    {
      id: 'va-003',
      name: 'Product Demo Videos',
      category: 'video-ads',
      description: 'Product demonstration video ad templates',
      preview: 'https://readdy.ai/api/search-image?query=Product%20demo%20video%20ad%20with%20product%20showcase%2C%20demonstration%20graphics%2C%20before%20and%20after%20visuals%2C%20product%20advertising&width=400&height=300&seq=va003&orientation=landscape',
      features: ['Product Focus', 'Demo Scripts', 'Feature Highlights', 'Conversion Tracking'],
      platforms: ['YouTube', 'Facebook', 'Instagram', 'LinkedIn'],
      isPremium: true
    },

    // Print Ads
    {
      id: 'pa-001',
      name: 'Magazine Advertisement',
      category: 'print-ads',
      description: 'Full-page magazine ad templates with professional layouts',
      preview: 'https://readdy.ai/api/search-image?query=Magazine%20advertisement%20design%20with%20professional%20print%20layout%2C%20high-quality%20graphics%2C%20magazine%20format%2C%20print%20advertising%20design&width=300&height=400&seq=pa001&orientation=portrait',
      features: ['Print Quality', 'Full Page Layout', 'Professional Typography', 'Brand Integration'],
      platforms: ['Magazines', 'Newspapers', 'Trade Publications'],
      isPremium: false
    },
    {
      id: 'pa-002',
      name: 'Newspaper Classified',
      category: 'print-ads',
      description: 'Newspaper classified and display ad templates',
      preview: 'https://readdy.ai/api/search-image?query=Newspaper%20classified%20ad%20design%20with%20newspaper%20format%2C%20classified%20section%20layout%2C%20print%20advertising%2C%20black%20and%20white%20design&width=400&height=200&seq=pa002&orientation=landscape',
      features: ['Classified Format', 'Cost Effective', 'Local Targeting', 'Contact Focus'],
      platforms: ['Newspapers', 'Local Publications'],
      isPremium: false
    },
    {
      id: 'pa-003',
      name: 'Direct Mail Postcards',
      category: 'print-ads',
      description: 'Direct mail postcard advertising templates',
      preview: 'https://readdy.ai/api/search-image?query=Direct%20mail%20postcard%20design%20with%20mailing%20format%2C%20postcard%20layout%2C%20direct%20marketing%20graphics%2C%20postal%20advertising&width=400&height=300&seq=pa003&orientation=landscape',
      features: ['Postcard Format', 'Mailing Optimization', 'Response Tracking', 'Local Targeting'],
      platforms: ['Direct Mail', 'EDDM', 'Targeted Mailing'],
      isPremium: true
    }
  ];

  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUser(user);
        }
      } catch (error) {
        console.error('Error getting user:', error);
      }
    };

    getUser();
  }, [navigate]);

  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
  };

  const startCreating = (template: Template) => {
    // Navigate to marketing ads editor with selected template
    navigate(`/dashboard/marketing-ads/editor?template=${template.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-black text-gray-900 tracking-tight leading-none">
                AI BOOK AUTHORITY
              </Link>
            </div>
            <nav className="flex space-x-8">
              <Link to="/dashboard" className="text-gray-700 hover:text-blue-600 font-medium">
                Dashboard
              </Link>
              <Link to="/pricing" className="text-gray-700 hover:text-blue-600 font-medium">
                Pricing
              </Link>
              <button
                onClick={() => supabase.auth.signOut().then(() => navigate('/auth/login'))}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 whitespace-nowrap"
              >
                Sign Out
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Marketing Ads Service</h1>
          <p className="text-gray-600">Create compelling marketing advertisements for digital and print campaigns across multiple platforms</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-1/4">
            {/* Search */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-4">Search Templates</h3>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <i className="ri-search-line absolute left-3 top-2.5 text-gray-400"></i>
              </div>
            </div>

            {/* Categories */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Categories</h3>
              <div className="space-y-2">
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-blue-100 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{category.name}</span>
                      <span className="text-sm text-gray-500">{category.count}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Template Grid */}
          <div className="lg:w-3/4">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {selectedCategory === 'all' ? 'All Templates' : categories.find(c => c.id === selectedCategory)?.name}
                </h3>
                <span className="text-gray-600">{filteredTemplates.length} templates</span>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTemplates.map(template => (
                  <div
                    key={template.id}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <div className="aspect-w-4 aspect-h-3 bg-gray-100">
                      <img
                        src={template.preview}
                        alt={template.name}
                        className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-200"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 text-sm">{template.name}</h4>
                        {template.isPremium && (
                          <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                            Premium
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 text-xs mb-3">{template.description}</p>
                      
                      {/* Platforms */}
                      <div className="mb-3">
                        <div className="flex flex-wrap gap-1">
                          {template.platforms.slice(0, 2).map((platform, index) => (
                            <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                              {platform}
                            </span>
                          ))}
                          {template.platforms.length > 2 && (
                            <span className="text-xs text-gray-500">+{template.platforms.length - 2} more</span>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startCreating(template);
                        }}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors whitespace-nowrap"
                      >
                        Create Ad
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {filteredTemplates.length === 0 && (
                <div className="text-center py-12">
                  <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
                  <p className="text-gray-600">Try adjusting your search or category filter</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Template Preview Modal */}
        {selectedTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedTemplate.name}</h2>
                    <p className="text-gray-600 mt-1">{selectedTemplate.description}</p>
                  </div>
                  <button
                    onClick={() => setSelectedTemplate(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Template Preview</h3>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <img
                        src={selectedTemplate.preview}
                        alt={selectedTemplate.name}
                        className="w-full h-auto"
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Template Details</h3>
                    <div className="space-y-4">
                      <div>
                        <span className="font-medium text-gray-700">Category:</span>
                        <p className="text-gray-600 mt-1 capitalize">
                          {selectedTemplate.category.replace('-', ' ')}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Supported Platforms:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedTemplate.platforms.map((platform, index) => (
                            <span key={index} className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                              {platform}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Features:</span>
                        <ul className="text-gray-600 mt-1 space-y-1">
                          {selectedTemplate.features.map((feature, index) => (
                            <li key={index} className="flex items-center">
                              <i className="ri-check-line text-green-500 mr-2"></i>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">What You'll Get:</span>
                        <ul className="text-gray-600 mt-1 space-y-1">
                          <li>• Multi-platform ad formats</li>
                          <li>• A/B testing capabilities</li>
                          <li>• Performance analytics</li>
                          <li>• Campaign management tools</li>
                          <li>• Brand customization options</li>
                        </ul>
                      </div>
                    </div>

                    <div className="mt-8 space-y-3">
                      <button
                        onClick={() => startCreating(selectedTemplate)}
                        className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap"
                      >
                        Start Creating Ad
                      </button>
                      <button
                        onClick={() => setSelectedTemplate(null)}
                        className="w-full border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap"
                      >
                        Browse More Templates
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MarketingAdsPage;
